import { useEffect, useState } from "react";
import { Building2, Check, Loader2, ShieldQuestion, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";

const STATUS_COLORS = {
  pending: "hsl(var(--watch))",
  approved: "hsl(var(--healthy))",
  rejected: "hsl(var(--at-risk))",
};

const Pill = ({ status }) => (
  <span
    className="border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
    style={{ borderColor: STATUS_COLORS[status], color: STATUS_COLORS[status] }}
  >
    {status === "pending" ? "with SDTD" : status}
  </span>
);

const useAccess = () => {
  const [data, setData] = useState(null);
  const load = () =>
    api
      .get("/access/requests")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  useEffect(() => {
    load();
  }, []);
  return [data, load];
};

/** On My profile: an officer asks SDTD for another unit's data. */
export const AccessRequestPanel = () => {
  const [data, load] = useAccess();
  const [picked, setPicked] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!data || !data.scoped) return null;

  const askable = data.units.filter((u) => !data.my_units.includes(u.code));
  const waiting = data.requests.find((r) => r.status === "pending");

  const toggle = (code) =>
    setPicked((p) => (p.includes(code) ? p.filter((x) => x !== code) : [...p, code]));

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/access/requests", { plants: picked, reason });
      toast.success("Sent to SDTD. You will get a mail as soon as it is decided.");
      setPicked([]);
      setReason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 border border-border bg-card p-5" data-testid="access-request-panel">
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-primary" />
        <div className="label-caps">See another unit</div>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        You see <b>{data.my_units.join(", ")}</b> today. If your work needs another unit's use
        cases, ask SDTD — an administrator has to agree, and the access stays until it is withdrawn.
      </p>

      {waiting ? (
        <div className="mt-4 border border-[hsl(var(--watch))] p-3 text-sm" data-testid="access-waiting">
          <Pill status="pending" />
          <span className="ml-2">
            You have asked for {waiting.plants.join(", ")}. SDTD has it.
          </span>
        </div>
      ) : askable.length ? (
        <>
          <div className="mt-4 flex flex-wrap gap-2">
            {askable.map((u) => (
              <button
                type="button"
                key={u.code}
                data-testid={`ask-unit-${u.code}`}
                onClick={() => toggle(u.code)}
                className={`border px-3 py-1.5 text-xs font-medium transition-colors duration-200 ${
                  picked.includes(u.code)
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                }`}
              >
                {u.code} — {u.name}
              </button>
            ))}
          </div>
          <Input
            className="mt-3 h-11 rounded-none"
            data-testid="access-reason"
            placeholder="Why do you need it? e.g. I look after the Mines use cases as well."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button
            className="mt-3 h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            data-testid="send-access-request"
            disabled={busy || !picked.length || reason.trim().length < 10}
            onClick={send}
          >
            {busy ? "Sending…" : "Ask SDTD for this access"}
          </Button>
        </>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">
          You already have every unit the portal can give you.
        </p>
      )}

      {!!data.requests.length && (
        <div className="mt-5">
          <div className="label-caps">What you have asked for</div>
          <ul className="mt-2 divide-y divide-border border border-border">
            {data.requests.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 p-3 text-xs">
                <Pill status={r.status} />
                <b>{r.plants.join(", ")}</b>
                <span className="text-muted-foreground">{r.reason}</span>
                {r.note && <span className="text-muted-foreground">· SDTD: {r.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/** On the Users screen: SDTD approves or refuses, and can withdraw what was given. */
export const AccessQueue = () => {
  const [data, load] = useAccess();
  const [note, setNote] = useState({});
  const [busy, setBusy] = useState("");

  if (!data || !data.can_decide) return null;
  const pending = data.requests.filter((r) => r.status === "pending");

  const decide = async (r, decision) => {
    setBusy(r.id);
    try {
      await api.post(`/access/requests/${r.id}/decide`, { decision, note: note[r.id] || "" });
      toast.success(
        decision === "approve"
          ? `${r.name} can now see ${r.plants.join(", ")}.`
          : `Refused, and ${r.name} has been told.`,
      );
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const withdraw = async (g, plant) => {
    if (!window.confirm(`Withdraw ${plant} from ${g.name}?`)) return;
    try {
      await api.delete(`/access/granted/${encodeURIComponent(g.email)}/${plant}`);
      toast.success(`${plant} withdrawn from ${g.name}.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="mt-8 border border-border bg-card p-5" data-testid="access-queue">
      <div className="flex items-center gap-2">
        <ShieldQuestion className="h-4 w-4 text-primary" />
        <div className="label-caps">Requests to see another unit</div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        A SPOC sees only their own unit. When one of them needs another unit's use cases, the request
        lands here — nothing changes until you agree, and you can withdraw it later.
      </p>

      {pending.length ? (
        <ul className="mt-4 divide-y divide-border border border-border">
          {pending.map((r) => (
            <li key={r.id} className="p-4" data-testid={`access-request-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <b>{r.name}</b>
                <span className="font-data text-xs text-muted-foreground">{r.email}</span>
                <span className="text-xs text-muted-foreground">
                  sees {r.own_plant} · asks for <b>{r.plants.join(", ")}</b>
                </span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{r.reason}</div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Input
                  className="h-10 max-w-sm rounded-none text-xs"
                  data-testid={`access-note-${r.id}`}
                  placeholder="A line back to the officer (optional)"
                  value={note[r.id] || ""}
                  onChange={(e) => setNote((n) => ({ ...n, [r.id]: e.target.value }))}
                />
                <Button
                  className="h-10 rounded-none font-display text-[11px] font-bold uppercase tracking-widest"
                  data-testid={`approve-access-${r.id}`}
                  disabled={busy === r.id}
                  onClick={() => decide(r, "approve")}
                >
                  <Check className="mr-1.5 h-3.5 w-3.5" /> Approve
                </Button>
                <Button
                  variant="outline"
                  className="h-10 rounded-none font-display text-[11px] font-bold uppercase tracking-widest"
                  data-testid={`reject-access-${r.id}`}
                  disabled={busy === r.id}
                  onClick={() => decide(r, "reject")}
                >
                  <X className="mr-1.5 h-3.5 w-3.5" /> Refuse
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">Nothing is waiting on you.</p>
      )}

      {!!data.granted.length && (
        <div className="mt-5">
          <div className="label-caps">Extra units already given</div>
          <ul className="mt-2 divide-y divide-border border border-border">
            {data.granted.map((g) => (
              <li key={g.email} className="flex flex-wrap items-center gap-2 p-3 text-xs">
                <b>{g.name}</b>
                <span className="font-data text-muted-foreground">{g.email}</span>
                <span className="text-muted-foreground">own unit {g.own_plant || "—"}</span>
                {g.plants.map((p) => (
                  <button
                    type="button"
                    key={p}
                    data-testid={`withdraw-${g.email}-${p}`}
                    title="Withdraw this unit"
                    onClick={() => withdraw(g, p)}
                    className="border border-[hsl(var(--healthy))] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--healthy))] transition-colors duration-200 hover:border-destructive hover:text-destructive"
                  >
                    {p} ×
                  </button>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const AccessLoading = () => (
  <div className="mt-8 flex h-16 items-center justify-center">
    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
  </div>
);
